import {
  apiFetchActiveSites,
  apiPostKit,
  apiPostSite,
  apiUpdateKit,
  apiUpdateSiteStatus,
  assignKitToUser,
  assignSiteToUser,
  createCluster,
  fetchClusterData,
  fetchKitData,
  fetchSiteData,
  updateCluster,
  updateSite,
} from "@/services/clusterSiteKit.service";
import type {
  ClusterData,
  KitData,
  SiteData,
} from "@/types/clusterSiteKit.types";
import type { User } from "@/types/user.types";
import { errorHandler } from "@/utils/errorHandler";
import { defineStore } from "pinia";
import { apiUpdateClusterStatus } from "../services/clusterSiteKit.service";
import { useGlobalStore } from "./globalStore";

export const useClusterSiteKitStore = defineStore("clusterSiteKitStore", {
  state: () => ({
    clusterSites: [] as SiteData[],
    activeSites: [] as SiteData[],
    user: {} as User,
    users: {} as User[],
    pendingSites: [] as string[],
    pendingClusterSites: [] as SiteData[],

    // Cluster Management
    clusters: [] as ClusterData[],
    cluster: {} as ClusterData,
    clusterName: "",
    clusterId: "",

    // Site Management
    sites: [] as SiteData[],
    site: {} as SiteData | null,
    siteName: "",
    siteId: "",

    // Kit Management
    kits: [] as KitData[],
    kitName: "",
    kitId: "",

    // Pagination & Search
    currentPage: 1,
    total: 0,
    search: "",
    searchCluster: "",
    searchKit: "",
    searchSite: "",
    searchPhotographer: "",
    limit: 10,
  }),

  actions: {
    // KIT MANAGEMENT

    async fetchKitData(newLimit?: number,getSystem?:boolean) {
      const global = useGlobalStore();
      global.showSkeletonLoading = true;
      this.kits = [];
      try {
        const res = await fetchKitData(
          newLimit || this.limit,
          this.currentPage,
          this.searchKit,
          getSystem
        );
        if (!res) {
          console.log("no kit data found");
          return;
        }
        this.kits = res.items;
        this.total = res.total;
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      } finally {
        global.showSkeletonLoading = false;
      }
    },

    async postKit(name: string, user: User) {
      const global = useGlobalStore();
      global.showLoader = true;
      const existsKit = this.kits.some((kit) => kit.name === name);
      if (!name) {
        throw new Error("Le nom du kit ne peut pas être vide.");
      }

      if (existsKit) {
        throw new Error("Ce nom de kit est déjà utilisé.");
      }

      try {
        await apiPostKit(name, user);
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      } finally {
        this.kitName = "";
        global.showLoader = false;
      }
    },

    async updateKit(id: string, name: string, isActive: boolean) {
      const global = useGlobalStore();
      global.showLoader = true;
      const kit = this.kits.find((k) => k.id === id);
      const existsKit = this.kits.some((k) => k.name === name && k.id !== id);

      if (!name) {
        throw new Error("Le nom du kit ne peut pas être vide.");
      }

      if (existsKit) {
        throw new Error("Ce nom de kit est déjà utilisé.");
      }
      if (!kit) {
        throw new Error("Kit introuvable.");
      }
      try {
        await apiUpdateKit(
          id,
          name,
          typeof isActive === "boolean" ? isActive : kit.isActive
        );
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      } finally {
        global.showLoader = false;
      }
    },

    // SITE MANAGEMENT

    async fetchSiteData(newLimit?: number,getSystem?:boolean) {
      const global = useGlobalStore();
      global.showSkeletonLoading = true;
      try {
        const res = await fetchSiteData(
          newLimit || this.limit,
          this.currentPage,
          this.searchSite,
          getSystem
        );

        if (!res) {
          console.log("no sites data found");
          return;
        }
        this.sites = res.items;
        this.total = res.total;
      } catch (error) {
        this.sites = [];
        this.total = 0;
        if (error instanceof Error) {
          errorHandler(error);
        }
      } finally {
        global.showSkeletonLoading = false;
      }
    },

    async fetchActiveSites() {
      const global = useGlobalStore();
      global.showLoader = false;
      try {
        const res = await apiFetchActiveSites();
        if (!res) {
          console.log("no sites data found");
          return;
        }
        this.activeSites = res.items;
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      } finally {
        global.showLoader = false;
      }
    },

    addToPendingSites() {
      if (!this.siteName) {
        return;
      }
      const name = this.siteName.trim();
      const existsSiteName = this.sites.some((site) => site.name === name);
      const existSiteInPending = this.pendingSites.includes(name);
      if (name && !existsSiteName && !existSiteInPending) {
        this.pendingSites.push(name);
        this.siteName = "";
      } else {
        throw new Error("Ce nom de site est déjà utilisé.");
      }
    },

    async postSite(name: string) {
      const global = useGlobalStore();
      global.showLoader = true;
      try {
        await apiPostSite(name);
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      } finally {
        global.showLoader = false;
      }
    },

    async updateSite(name: string) {
      const global = useGlobalStore();
      global.showLoader = true;
      try {
        const existsSiteName = this.sites.some((site) => site.name === name);
        if (!existsSiteName) {
          await updateSite(this.siteId, name);
          await this.fetchSiteData();
        } else {
          throw new Error("Ce nom de site est déjà utilisé.");
        }
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      } finally {
        this.siteName = "";
        global.showLoader = false;
      }
    },

    async updateSiteStatus(isActive: boolean) {
      const global = useGlobalStore();
      global.showLoader = true;
      try {
        await apiUpdateSiteStatus(this.siteId, isActive);
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      } finally {
        global.showLoader = false;
      }
    },

    // CLUSTER MANAGEMENT

    async updateClusterStatus(isValid: boolean) {
      const global = useGlobalStore();
      global.showLoader = true;
      try {
        const cluster = this.clusters.find(
          (cluster) => cluster.id === this.clusterId
        );
        if (!cluster) {
          throw new Error("Ce cluster est introuvable.");
        }
        await apiUpdateClusterStatus(cluster.id, isValid);
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      } finally {
        global.showLoader = false;
      }
    },

    async fetchClusterData(limit?:number,getSystem?:boolean) {
      const global = useGlobalStore();
      global.showSkeletonLoading = true;
      try {
        const res = await fetchClusterData(
          limit || this.limit,
          this.currentPage,
          this.searchCluster,
          getSystem
        );

        if (!res) {
          console.log("no cluster data found");
          return;
        }

        this.clusters = res.items;
        this.total = res.total;
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      } finally {
        global.showSkeletonLoading = false;
      }
    },

    getClusterData() {
      const clusterData = this.clusters.find(
        (cluster) => cluster.id === this.clusterId
      );

      if (clusterData) {
        const clusterSite = clusterData.sites;
        return clusterSite;
      }
    },

    addToClusterPendingSites() {
      const name = this.siteName.trim();
      if (!name) {
        return;
      }

      const foundSite = this.sites.some((site) => site.name === name);
      if (!foundSite) {
        throw new Error("Ce site est introuvable.");
      }

      const existSiteInPending = this.pendingClusterSites.some(
        (site) => site.name === name
      );

      if (existSiteInPending) {
        throw new Error("Ce site est déjà dans la liste en attente.");
      }

      const newSite: SiteData = {
        name: name,
        id: "",
        status: "",
        isActive: true,
      };

      this.pendingClusterSites.push(newSite);
      this.siteName = "";
    },

    async postCluster() {
      const global = useGlobalStore();
      global.showLoader = true;
      const name = this.clusterName.trim();
      const existsClusterIndb = this.clusters.some(
        (cluster) => cluster.name === name
      );

      if (!name) {
        throw new Error("Le nom du cluster ne peut pas être vide.");
      }

      if (existsClusterIndb) {
        throw new Error("Ce nom de cluster est déjà utilisé.");
      }

      if (this.pendingClusterSites.length === 0) {
        throw new Error("Un cluster doit contenir au moins un site.");
      }

      const sitesIds = this.pendingClusterSites.map((site) => site.id);

      try {
        await createCluster(name, sitesIds);

        this.clusterName = "";
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      } finally {
        global.showLoader = false;
      }
    },

    async updateCluster(clusterId: string, name: string, sitesIds?: string[]) {
      const global = useGlobalStore();
      global.showLoader = true;
      try {
        const cluster = this.cluster;
        const existsClusterIndb = this.clusters.some(
          (cluster) => cluster.name === name && cluster.id !== clusterId
        );

        if (existsClusterIndb) {
          throw new Error("Ce nom de cluster est déjà utilisé.");
        }

        if (!sitesIds && cluster.sites.length > 0) {
          sitesIds = cluster.sites.map((site) => site.id);
        }
        await updateCluster(clusterId, name, sitesIds);
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      } finally {
        global.showLoader = false;
      }
    },

    async removeSitefromCluster(site: SiteData, clusterId: string) {
      const global = useGlobalStore();
      global.showLoader = true;
      const cluster = this.clusters.find((cluster) => cluster.id === clusterId);
      if (!cluster) {
        return;
      }
      const name = cluster.name;
      const updatedSites = cluster.sites.filter((s) => s.id !== site.id);
      const sitesIds = updatedSites.map((s) => s.id);
      try {
        await updateCluster(clusterId, name, sitesIds);
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      } finally {
        global.showLoader = false;
      }
    },

    async assignSiteToUser( userId: string, siteId: string) {
      const global = useGlobalStore();
      global.showLoader = true;
      if (!siteId || !userId) {
        throw new Error("Site ou utilisateur introuvable.");
      }
      try {
         await assignSiteToUser(userId,siteId);
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      } finally {
        global.showLoader = false;
      }
    },

    async assignKitToUser( userId: string, kitId: string) {
      const global = useGlobalStore();
      global.showLoader = true;
      if (!kitId || !userId) {
        throw new Error("Kit ou utilisateur introuvable.");
      }
      try {
         await assignKitToUser(userId,kitId);
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      } finally {
        global.showLoader = false;
      }
    },

    // OTHER METHODS

    stringToColor(str: string): string {
      // Fonction de hachage pour convertir la chaîne en un nombre
      let hash = 0;
      for (let i = 0; i < str?.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
      }

      // Conversion du hash en couleur RGB
      const r = (hash >> 16) & 0xff;
      const g = (hash >> 8) & 0xff;
      const b = hash & 0xff;

      // Conversion en format hexadécimal
      return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
    },

    emptyStore() {
      this.clusterSites = [];
      this.activeSites = [];
      this.user = {} as User;
      this.users = [] as User[];
      this.pendingSites = [];
      this.pendingClusterSites = [];
      this.clusters = [] as ClusterData[];
      this.cluster = {} as ClusterData;
      this.clusterName = "";
      this.clusterId = "";
      this.sites = [] as SiteData[];
      this.site = null;
      this.siteName = "";
      this.siteId = "";
      this.kits = [] as KitData[];
      this.kitName = "";
      this.kitId = "";
      this.currentPage = 1;
      this.total = 0;
      this.search = "";
    },

    resetSiteData() {
      this.sites = [];
      this.site = null;
      this.siteName = "";
      this.siteId = "";
      this.pendingSites = [];
      this.currentPage = 1;
      this.total = 0;
      this.search = "";
    },

    resetKitData() {
      this.kits = [];
      this.kitName = "";
      this.kitId = "";
      this.currentPage = 1;
      this.total = 0;
      this.search = "";
    },

    resetClusterData() {
      this.clusters = [];
      this.cluster = {} as ClusterData;
      this.clusterName = "";
      this.clusterId = "";
      this.pendingClusterSites = [];
      this.currentPage = 1;
      this.total = 0;
      this.search = "";
    },
  },
});
